import * as Yup from 'yup';
import { TFunction } from 'i18next';
import { JPOnboardingSteps } from './use-job-positions-onboarding';
import tw, { styled } from 'twin.macro';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ValidationSchema = Yup.ObjectSchema<any>;

/**
 * Step-specific validation schemas
 * Each step validates only the fields that are shown/required in that step
 *
 * @param t - Translation function from useTranslation hook
 * @returns Record of validation schemas for each step
 */
export const getStepValidationSchemas = (t: TFunction): Record<JPOnboardingSteps, ValidationSchema> => ({
  venue_selection: Yup.object().shape({
    restaurant_id: Yup.string().required(t('errors.venue_required')),
  }) as ValidationSchema,

  venue_onboarding: Yup.object().shape({
    // No validation needed - venue onboarding has its own internal validation
  }) as ValidationSchema,

  role: Yup.object().shape({
    domain_interest: Yup.string().required(t('errors.domain_interest_required')),
    occupation_id: Yup.string().required(t('steps.role.occupation_required')),
    experience: Yup.string().required(t('steps.role.experience_required')),
    experience_var: Yup.mixed().nullable(),
    experience_specifics: Yup.string()
      .nullable()
      .when('experience_var', {
        is: (val: { key?: string } | null) => val?.key && ['zero', 'experience_jp_zero'].includes(val.key),
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.required(t('steps.role.experience_specifics_required')),
      }),
  }) as ValidationSchema,

  skills: Yup.object().shape({
    languages: Yup.array()
      .of(
        Yup.object().shape({
          language: Yup.string().required(t('errors.required_field')),
          level: Yup.string().required(t('errors.required_field')),
        })
      )
      .nullable(),
    // Add validation for skills fields
    // required_skills: Yup.array().min(1, 'Please select at least one skill'),
  }) as ValidationSchema,

  other_requirements: Yup.object().shape(
    {
      staff_requested: Yup.number()
        .required(t('errors.required_field'))
        .typeError(t('steps.other_requirements.staff_requested.validation_numeric'))
        .positive(t('steps.other_requirements.staff_requested.validation_positive'))
        .integer(t('steps.other_requirements.staff_requested.validation_integer')),
      advertised_location: Yup.string().required(t('errors.required_field')),
      ['seasonal?']: Yup.boolean().required(t('errors.required_field')),
      desired_start_type: Yup.string()
        .nullable()
        .when('seasonal?', {
          is: false,
          then: (schema) =>
            schema
              .required(t('errors.required_field'))
              .oneOf(['asap', 'exact_date', 'indicative_date'], t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      desired_start_date: Yup.string()
        .nullable()
        .when('desired_start_type', {
          is: (val: string) => val === 'exact_date' || val === 'indicative_date',
          then: (schema) => schema.required(t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
    },
    [['desired_start_type', 'desired_start_date']]
  ) as ValidationSchema,

  contract_and_shifts: Yup.object().shape({
    contract_type: Yup.string().required(t('errors.required_field')),
    job_type: Yup.string().required(t('errors.required_field')),
    days_required_per_week_number: Yup.number().required(t('errors.required_field')),
    shift_category: Yup.string().required(t('errors.required_field')),
  }) as ValidationSchema,

  salary: Yup.object()
    .shape({
      payment_type: Yup.string().required(t('errors.required_field')),
      payment_type_var: Yup.mixed().nullable(),
      min_salary: Yup.number()
        .nullable()
        .typeError(t('steps.salary.salary_validation_numeric'))
        .positive(t('steps.salary.salary_validation_positive')),
      max_salary: Yup.number()
        .nullable()
        .typeError(t('steps.salary.salary_validation_numeric'))
        .positive(t('steps.salary.salary_validation_positive')),
      salary: Yup.number()
        .nullable()
        .typeError(t('steps.salary.salary_validation_numeric'))
        .positive(t('steps.salary.salary_validation_positive')),
      // salary_type: Yup.string().required(t('errors.required_field')),
      // salary_month_number: Yup.number().required(t('errors.required_field')),
    })
    .test('salary-validation', '', function (value) {
      const { salary, min_salary, max_salary } = value || {};

      // If exact salary is provided, min and max are not required
      if (salary !== null && salary !== undefined) {
        return true;
      }

      const hasMin = min_salary !== null && min_salary !== undefined;
      const hasMax = max_salary !== null && max_salary !== undefined;

      // Min only or both min+max - OK
      if (hasMin) {
        return true;
      }

      // Only max without min - blocked ("fino a..." is not allowed)
      if (hasMax && !hasMin) {
        return this.createError({
          path: 'min_salary',
          message: t('steps.salary.salary_min_required'),
        });
      }

      // Neither salary nor min/max are provided - validation fails
      const error = this.createError({
        message: t('errors.required_field'),
      });
      error.inner = [
        this.createError({
          path: 'salary',
          message: t('errors.required_field'),
        }),
        this.createError({
          path: 'min_salary',
          message: t('errors.required_field'),
        }),
        this.createError({
          path: 'max_salary',
          message: t('errors.required_field'),
        }),
      ];
      return error;
    })
    .test('salary-sanity-check', '', function (value) {
      const { salary, min_salary, max_salary, payment_type_var } = value || {};
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const paymentTypeLabel = (payment_type_var as any)?.label?.toLowerCase() || '';
      const isHourly = paymentTypeLabel.includes('ore') || paymentTypeLabel.includes('orari');
      const isAnnual = paymentTypeLabel.includes('annual') || paymentTypeLabel.includes('ral');
      const threshold = isHourly ? 4 : isAnnual ? 5000 : 100;
      const errorMsg = isHourly
        ? t('steps.salary.salary_too_low_hourly')
        : isAnnual
        ? t('steps.salary.salary_too_low_annual')
        : t('steps.salary.salary_too_low_monthly');

      const valuesToCheck = [salary, min_salary, max_salary].filter(
        (v): v is number => v !== null && v !== undefined && v > 0
      );

      if (valuesToCheck.length === 0) return true;

      const errors: Yup.ValidationError[] = [];
      if (salary !== null && salary !== undefined && salary > 0 && salary < threshold) {
        errors.push(this.createError({ path: 'salary', message: errorMsg }));
      }
      if (min_salary !== null && min_salary !== undefined && min_salary > 0 && min_salary < threshold) {
        errors.push(this.createError({ path: 'min_salary', message: errorMsg }));
      }
      if (max_salary !== null && max_salary !== undefined && max_salary > 0 && max_salary < threshold) {
        errors.push(this.createError({ path: 'max_salary', message: errorMsg }));
      }

      if (errors.length > 0) {
        const error = this.createError({ message: errorMsg });
        error.inner = errors;
        return error;
      }

      return true;
    }) as ValidationSchema,

  benefits: Yup.object().shape({
    // Add validation for benefits fields (usually optional)
    // Example:
    // benefits: Yup.array(),
  }) as ValidationSchema,

  additional_notes: Yup.object().shape({
    // No validation needed - notes are optional
  }) as ValidationSchema,

  ai_autogeneration: Yup.object().shape({
    job_title: Yup.string().required(t('errors.required_field')),
    job_description: Yup.string().required(t('errors.required_field')),
    job_contract_description: Yup.string().required(t('errors.required_field')),
  }) as ValidationSchema,

  review: Yup.object().shape({
    // Final review step - validate all required fields are present
    // This ensures nothing was missed
    restaurant_id: Yup.string().required(t('errors.venue_required')),
    domain_interest: Yup.string().required(t('errors.domain_interest_required')),
    // Add other critical fields that must be present
  }) as ValidationSchema,

  company_and_payment: Yup.object().shape({
    // No validation needed - handled via sub-views within the step
  }) as ValidationSchema,

  success: Yup.object().shape({
    // No validation needed - success is a read-only confirmation screen
  }) as ValidationSchema,
});

/**
 * Helper function to get validation schema for a specific step
 * @param step - The current onboarding step
 * @param t - Translation function from useTranslation hook
 */
export const getValidationSchemaForStep = (step: JPOnboardingSteps, t: TFunction): ValidationSchema => {
  const schemas = getStepValidationSchemas(t);
  return schemas[step];
};

/**
 * Complete validation schema for the entire form
 * Used only when submitting the final form
 * @param t - Translation function from useTranslation hook
 */
export const getCompleteJobPositionSchema = (t: TFunction) =>
  Yup.object().shape({
    restaurant_id: Yup.string().required(t('errors.venue_required')),
    domain_interest: Yup.string().required(t('errors.domain_interest_required')),
    // Add all other required fields from all steps
    // occupation_id: Yup.string().required(t('errors.occupation_required')),
    // contract_type: Yup.string().required(t('errors.contract_type_required')),
    // salary_min: Yup.number().required(t('errors.salary_min_required')).positive(t('errors.salary_positive')),
    // ... etc
  });

export const ErrorMessage = styled.span`
  ${tw`mt-1 text-sm text-red-600 font-normal`}
`;
